import React, { useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { advanceOnboardingStateAction } from 'Shared/actions/onboardingState';

import OnboardingWizardPageWrapper from './OnboardingWizardWrappers/OnboardingWizardPageWrapper/OnboardingWizardPageWrapper';
import ZoomOnboardingPageWrapper from './ZoomOnboardingPageWrapper/ZoomOnboardingPageWrapper';
import ManagerAssistantSettings from './ManagerAssistantSettings/ManagerAssistantSettings';
import DiscussionTopicsForInvited from './DiscussionTopicsForInvited/DiscussionTopicsForInvited';
import DiscussionTopics from './DiscussionTopics/DiscussionTopics';
import CreateAccount from './CreateAccount/CreateAccount';
import ZoomCreateAccount from './ZoomCreateAccount/ZoomCreateAccount';
import MagicOneOnOneCreateAccount from './MagicOneOnOneCreateAccount/MagicOneOnOneCreateAccount';
import Workspaces from './Workspaces/Workspaces';
import Profile from './Profile/Profile';
import ConnectCalendar from './ConnectCalendar/ConnectCalendar';
import WorkspacesFromCalendar from './WorkspacesFromCalendar/WorkspacesFromCalendar';
import Kudos from './Kudos/Kudos';
import ZoomWelcomePage from './ZoomWelcomePage/ZoomWelcomePage';
import Terminal from '../Terminal/Terminal';
import { OnboardingWizardStep, OnboardingState } from '../types';

function DesktopOnboardingWizard() {
  const {
    kudos,
    profile,
    workspaces,
    discussionTopics,
    step,
    managerAssistantSettings,
    numDirectReports,
    invitationInformation,
    userCreatedInMiddle,
    cannotSignIntoGoogle,
    flowType,
  }: OnboardingState = useSelector(state => state.onboardingState);
  const dispatch = useDispatch();

  if (step) {
    (window as any).analytics.identify({ flowType });
    (window as any).analytics.track('Started Onboarding Step', { step }, { integrations: { Calixa: false } });
  }

  const handleAdvanceOnboardingState = useCallback(
    update => dispatch(advanceOnboardingStateAction({ onboardingState: update, didSkip: false })),
    [dispatch]
  );

  const currentContent = () => {
    switch (step) {
      case OnboardingWizardStep.workspaces:
        return (
          <Workspaces
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            numDirectReports={numDirectReports}
          />
        );
      case OnboardingWizardStep.discussionTopics:
        return (
          <DiscussionTopics
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            discussionTopics={discussionTopics}
          />
        );
      case OnboardingWizardStep.discussionTopicsForInvited:
        return (
          <DiscussionTopicsForInvited
            advanceOnboardingState={handleAdvanceOnboardingState}
            workspaces={workspaces}
            discussionTopics={discussionTopics}
            invitationInfo={invitationInformation}
          />
        );
      case OnboardingWizardStep.kudos:
        return <Kudos kudos={kudos} advanceOnboardingState={handleAdvanceOnboardingState} workspaces={workspaces} />;
      case OnboardingWizardStep.managerAssistantSettings:
        return (
          <ManagerAssistantSettings
            workspaces={workspaces}
            managerAssistantSettings={managerAssistantSettings}
            advanceOnboardingState={handleAdvanceOnboardingState}
          />
        );
      case OnboardingWizardStep.workspacesFromCalendar:
        return <WorkspacesFromCalendar advanceOnboardingState={handleAdvanceOnboardingState} workspaces={workspaces} />;
      default:
        return null;
    }
  };

  if (step === OnboardingWizardStep.createAccount)
    return <CreateAccount workspaces={workspaces} profile={profile} cannotSignIntoGoogle={cannotSignIntoGoogle} />;
  if (step === OnboardingWizardStep.terminal) return <Terminal />;
  if (step === OnboardingWizardStep.profile) {
    return <Profile advanceOnboardingState={handleAdvanceOnboardingState} profile={profile} />;
  }
  if (step === OnboardingWizardStep.connectCalendar)
    return (
      <ConnectCalendar
        OAuthOnlyWithCalendar={flowType === 'force_calendar_integration_signup'}
        userCreatedInMiddle={userCreatedInMiddle}
        cannotSignIntoGoogle={cannotSignIntoGoogle}
      />
    );

  if (step === OnboardingWizardStep.magicOneOnOneCreateAccount)
    return <MagicOneOnOneCreateAccount profile={profile} cannotSignIntoGoogle={cannotSignIntoGoogle} />;

  // Zoom Onboarding
  if (step === OnboardingWizardStep.zoomCreateAccount)
    return (
      <ZoomOnboardingPageWrapper>
        <ZoomCreateAccount profile={profile} />
      </ZoomOnboardingPageWrapper>
    );
  if (step === OnboardingWizardStep.zoomProfileAndWorkspaces || step === OnboardingWizardStep.zoomProfile) {
    return <ZoomWelcomePage />;
  }

  return <OnboardingWizardPageWrapper>{currentContent()}</OnboardingWizardPageWrapper>;
}

export default memo(DesktopOnboardingWizard);
